import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const bankNameMap = {
  'shinhan': '신한은행',
  'kb': 'KB국민은행',
  'kookmin': 'KB국민은행',
  'woori': '우리은행',
  'hana': '하나은행',
  'nh': 'NH농협은행',
  'nonghyup': 'NH농협은행',
  'ibk': 'IBK기업은행',
  'sc': 'SC제일은행',
  'citi': '한국씨티은행',
  'kakao': '카카오뱅크',
  'kakaobank': '카카오뱅크',
  'toss': '토스뱅크',
  'tossbank': '토스뱅크',
  'kbank': '케이뱅크',
  'post': '우체국',
  'saemaul': '새마을금고',
  'credit': '신협',
  'suhyup': '수협은행',
  'busan': '부산은행',
  'daegu': '대구은행',
  'kwangju': '광주은행',
  'jeonbuk': '전북은행',
  'jeju': '제주은행',
  'kyongnam': '경남은행'
};

const getBankNameKorean = (bankName) => {
  if (!bankName) return '';
  const lower = bankName.toLowerCase().trim();
  return bankNameMap[lower] || bankName;
};

const PaymentInfoCard = ({ paymentInfo }) => {
  const [isCopied, setIsCopied] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    })?.format(amount);
  };

  const handleCopyAccount = async () => {
    try {
      await navigator.clipboard?.writeText(paymentInfo?.accountNumber);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDeadline = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 lg:p-8 shadow-md">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="CreditCard" size={20} color="var(--color-accent)" className="md:w-6 md:h-6" />
        </div>
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          참가비 안내
        </h3>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm md:text-base text-muted-foreground">
              {paymentInfo?.pricingModel === 'split' ? '1인당 참가비' : '참가비'}
            </span>
            <span className="text-2xl md:text-3xl font-heading font-bold text-primary data-text">
              {formatCurrency(paymentInfo?.memberPrice)}
            </span>
          </div>
          {paymentInfo?.guestPrice && (
            <div className="flex items-baseline justify-between pt-2 border-t border-primary/10">
              <span className="text-sm md:text-base text-muted-foreground">
                게스트 참가비
              </span>
              <span className="text-xl md:text-2xl font-heading font-bold text-accent data-text">
                {formatCurrency(paymentInfo?.guestPrice)}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="Building2" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm caption text-muted-foreground mb-1">
                은행
              </p>
              <p className="text-sm md:text-base font-medium text-foreground">
                {getBankNameKorean(paymentInfo?.bankName)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Icon name="Hash" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm caption text-muted-foreground mb-1">
                계좌번호
              </p>
              <div className="flex items-center gap-2">
                <p className="text-sm md:text-base font-medium text-foreground data-text break-all">
                  {paymentInfo?.accountNumber}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={isCopied ? 'Check' : 'Copy'}
                  onClick={handleCopyAccount}
                  className="flex-shrink-0"
                >
                  {isCopied ? '복사됨' : '복사'}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Icon name="User" size={18} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm caption text-muted-foreground mb-1">
                예금주
              </p>
              <p className="text-sm md:text-base font-medium text-foreground">
                {paymentInfo?.accountHolder}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Icon name="Clock" size={18} color="var(--color-warning)" className="flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="text-xs md:text-sm caption text-muted-foreground mb-1">
                입금 마감
              </p>
              <p className="text-sm md:text-base font-medium text-warning">
                {formatDeadline(paymentInfo?.paymentDeadline)}
              </p>
            </div>
          </div>
        </div>

        {paymentInfo?.notes && (
          <div className="bg-muted/50 rounded-lg p-3 md:p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} color="var(--color-muted-foreground)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm caption text-muted-foreground whitespace-pre-line">
                {paymentInfo?.notes}
              </p>
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-border">
          <div className="flex items-start gap-2 mb-3">
            <Icon name="AlertCircle" size={16} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm caption text-muted-foreground">
              입금 시 <span className="font-medium text-foreground">신청자 이름</span>으로 입금해주세요
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs md:text-sm caption text-muted-foreground">
              입금 확인 후 카카오톡으로 확정 안내를 보내드립니다
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentInfoCard;